import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import {
  TermsConditionResponse,
  PrivacyAndPolicyResponse,
  BannerResponse,
} from "./../Helpers/Interfaces/SettingType";

import { setBannerData } from "./../Redux/Slices/SettingSlice";
import Config from "./../Config";

export const SettingsService = createApi({
  reducerPath: "SettingsService",
  baseQuery: RtkBaseQueryWithReauth(Config.BASE_URL),
  tagTypes: ["Settings", "Term", "PrivacyPolicy", "Banner", "BankDetails"],
  keepUnusedDataFor: 600,
  endpoints: (builder) => ({
    getTermsData: builder.query<any, any>({
      query: () => ({
        url: `${ApiPath.getTerms}`,
        method: "GET",
      }),
      transformResponse: (response: { data: TermsConditionResponse }) =>
        response.data,
      providesTags: ["Term"],
    }),

    getPrivacyPolicy: builder.query<any, any>({
      query: () => ({
        url: `${ApiPath.getPrivacyPolicy}`,
        method: "GET",
      }),
      transformResponse: (response: { data: PrivacyAndPolicyResponse }) =>
        response.data,
      providesTags: ["PrivacyPolicy"],
    }),

    getBannerList: builder.query<
      BannerResponse,
      { page?: number; pageSize?: number } | void
    >({
      // Sending page/pageSize takes the API's paginated branch, which caps the
      // result AND -- unlike the unpaginated branch -- does not drop banners
      // whose category has been deactivated or deleted. Omitting them is the
      // correct call for the storefront: every active banner, category checked.
      query: (args) => ({
        url: `${ApiPath.getBanners}`,
        method: "GET",
        params:
          args && args.page && args.pageSize
            ? { page: args.page, pageSize: args.pageSize }
            : undefined,
      }),
      // Dispatch setBannerData on successful query
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data }: any = await queryFulfilled; // Wait for the query to resolve

          dispatch(setBannerData(data.data)); // Dispatch setUser with the fetched user data
        } catch (error) {
          console.error("Error fetching user:", error);
          // Optional: handle errors, e.g., clear token or show a notification
        }
      },
      providesTags: ["Banner"],
    }),

    getBankDetails: builder.query<any, void>({
      query: () => ({
        url: `${ApiPath.getBankDetails}`,
        method: "GET",
      }),
      transformResponse: (response: { data: any }) => response.data,
      providesTags: ["BankDetails"],
    }),
  }),
});

export const {
  useGetTermsDataQuery,
  useGetPrivacyPolicyQuery,
  useGetBannerListQuery,
  useGetBankDetailsQuery,
} = SettingsService;
